import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { EmptyState } from './EmptyState';
import { Screens } from './MultiAgentsHomeHandler.enum';
import { AccountWithoutAds } from './AccountWithoutAds';
import { AccountWithOneDraft } from './AccountWithOneDraft';
import { Header } from './Header';
import { useChatbot } from '@/contexts/Chatbot';
import { useAiChat } from '@/hooks/AiChat';
import useLocale from '@/hooks/useLocale';
import * as Styles from './MultiAgentsHomeHandler.styles';
import { SocialMediaIcons } from 'gatorcomponents';
import { adsHandlerActions } from '@/redux/modules/adsHandler';

const MultiAgentsHomeHandler = () => {
  const dispatch = useDispatch();
  const { pages, drafts } = useSelector(state => state.adsHandler);
  const chatbot = useChatbot();
  const { triggerExternalMessage } = useAiChat();
  const { marketing: marketingLocale } = useLocale();
  const { multiAgentScreens: multiAgentScreensLocale } = marketingLocale;
  const { header: headerLocale } = multiAgentScreensLocale;

  const [currentScreen, setCurrentScreen] = useState(Screens.EMPTY_STATE);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedPageIndex, setSelectedPageIndex] = useState(0);

  useEffect(() => {
    dispatch(adsHandlerActions.pages.request());
    dispatch(adsHandlerActions.drafts.request());
  }, [dispatch]);

  useEffect(() => {
    if (drafts.status === 'success' && drafts.data) {
      const hasDrafts = drafts.data.drafts && drafts.data.drafts.length > 0;
      if (hasDrafts) {
        setCurrentScreen(Screens.WITH_ONE_DRAFT);
        return;
      }
    }

    if (pages.status === 'success') {
      const hasPages = pages.list && pages.list.length > 0;
      setCurrentScreen(hasPages ? Screens.EMPTY_ADS : Screens.EMPTY_STATE);
    }
  }, [pages.status, pages.list, drafts.status, drafts.data]);

  const currentPage = pages.list[selectedPageIndex];
  const platform = currentPage && currentPage.platform;
  const account = currentPage && currentPage.account;

  const SelectedAccountLabel = useMemo(() => {
    if (!platform || !account) {
      return null;
    }

    return (
      <Styles.SelectedAccountRow>
        {platform && (
          <Styles.SocialMediaWrapper $platform={platform}>
            <SocialMediaIcons icon={platform} color="#fff" size="sm" />
          </Styles.SocialMediaWrapper>
        )}
        <Styles.AccountName>
          {account}
        </Styles.AccountName>
      </Styles.SelectedAccountRow>
    );
  }, [platform, account]);

  const options = useMemo(() => {
    if (!SelectedAccountLabel) return [];
    return [{
      label: SelectedAccountLabel,
      value: SelectedAccountLabel,
    }];
  }, [SelectedAccountLabel]);


  const handleOpenGator = ({
    message,
  }) => {
    if (chatbot.isActive) {
      chatbot.setRoute('general');
    }
    triggerExternalMessage({ message, websocketType: 'multi-agent' });
  };

  const getAccountWithoutAdsProps = () => ({
    handleOpenGator,
  });

  const getAccountWithOneDraftProps = () => {
    const draft = (drafts.data && drafts.data.drafts && drafts.data.drafts.length > 0)
      ? drafts.data.drafts[0]
      : null;
    return {
      handleOpenGator,
      options,
      SelectedAccountLabel,
      draft,
    };
  };

  const screens = {
    [Screens.EMPTY_STATE]: <EmptyState />,
    [Screens.EMPTY_ADS]: <AccountWithoutAds {...getAccountWithoutAdsProps()} />,
    [Screens.WITH_ONE_DRAFT]: <AccountWithOneDraft {...getAccountWithOneDraftProps()} />,
  };

  const hasConnectedAccount = pages.list && pages.list.length > 0;

  const handleAccountChange = (selectedAccount) => {
    const index = pages.list.findIndex(page => page.id === selectedAccount.id);
    if (index !== -1) {
      setSelectedPageIndex(index);
    }
  };

  return (
    <Styles.Container>
      {hasConnectedAccount && (
        <Header
          selectedAccount={{
            name: account,
            type: platform,
          }}
          connectedAccounts={pages.list.map(page => ({
            id: page.id,
            name: page.account,
            type: page.platform,
            description: headerLocale.accountPlatformDescription(page.platform),
          }))}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onAccountChange={handleAccountChange}
        />
      )}
      {screens[currentScreen]}
    </Styles.Container>
  );
};

export default MultiAgentsHomeHandler;
